import { computed, defineAsyncComponent, defineComponent, onMounted, onUnmounted } from 'vue';
import { useMgaReportRealMoneyBalanceListStore } from '@/store/mga-real-money-balance/mga-real-money-balance.store';
import { storeToRefs } from 'pinia';
import { realMoneyBalancePlayerTable } from '@/models/mga-real-money-balance/mga-real-money-balance.model';
import { Refresh, Download, Filter } from '@element-plus/icons-vue';
import { PAGINATION_PROPERTIES } from '@/constants';
import ReportTimestamp from '@/components/report-timestamp/report-timestamp.vue';
import DownloadActions from '@/components/download-actions/download-actions.vue';
import { useTwoFactorStore } from '@/store/two-factor-dialog.store';
import { useTimestampStore } from '@/store/timestamp.store';
import { usePermissionsStore } from '@/store/permissions.store';
import {
  useMgaRealMoneyBalanceListCsvStore,
} from '@/store/mga-real-money-balance/mga-real-money-balance-list-csv.store';
import { MGA_REAL_MONEY_BALANCE_PERMISSIONS } from '@/models/app-internal/permissions.model';
import {
  useMgaRealMoneyBalanceListFilterStore,
} from '@/store/mga-real-money-balance/mga-real-money-balance-list-filter.store';
import MgaRealMoneyBalancePageListFilter
  from '@/layouts/mga-real-money-balance-page/mga-real-money-balance-page-list-filter/mga-real-money-balance-page-list-filter.vue';

const icons = {
  Refresh,
  Download,
  Filter,
};

export default defineComponent({
  name: 'MgaRealManyBalancePage',

  components: {
    TwoFactorAuthDialog: defineAsyncComponent(
      () => import('@/components/two-factor-auth-dialog/two-factor-auth-dialog.vue'),
    ),
    ReportTimestamp,
    DownloadActions,
    MgaRealMoneyBalancePageListFilter,
  },

  setup() {
    const mgaReportRealMoneyBalanceListStore = useMgaReportRealMoneyBalanceListStore();
    const {
      realMoneyBalancePlayerList,
      isRealMoneyBalancePlayerListLoading,
      currentPage,
      listItemsCount,
      itemsPerPage,
    } = storeToRefs(mgaReportRealMoneyBalanceListStore);
    const {
      getRealMoneyBalancePlayerList,
      updateCurrentPage,
      updateListSorting,
      updatePaginationLimit,
      setDefaultPaginationSettings,
      setDefaultSorting,
    } = mgaReportRealMoneyBalanceListStore;

    const twoFactorStore = useTwoFactorStore();
    const { isTwoFactorDialogVisible } = storeToRefs(twoFactorStore);
    const { setTwoFactorDialogVisibility } = twoFactorStore;

    const { setTimestampRequired } = useTimestampStore();

    const { getRealMoneyBalancePlayerListCsv } = useMgaRealMoneyBalanceListCsvStore();

    const { realMoneyBalancePermissions } = storeToRefs(usePermissionsStore());

    const mgaRealMoneyBalanceListFilterStore = useMgaRealMoneyBalanceListFilterStore();
    const { isFilterVisible, activeFiltersCount, reportPeriod } = storeToRefs(mgaRealMoneyBalanceListFilterStore);
    const {
      toggleFilterVisibility,
      setDefaultFilterFormData,
      setActiveFiltersCount,
      setReportPeriod,
      setFilterVisible,
    } = mgaRealMoneyBalanceListFilterStore;

    const openTwoFactorDialog = (isReportTimestampRequired: boolean): void => {
      setTimestampRequired(isReportTimestampRequired);
      setTwoFactorDialogVisibility(true);
    };

    const isTimestampAllowed = computed<boolean>(() => {
      return realMoneyBalancePermissions.value[
        MGA_REAL_MONEY_BALANCE_PERMISSIONS.REPORT_PERMISSION_GET_MGA_PLAYER_TIMESTAMP
      ];
    });

    const isReportDownloadAllowed = computed<boolean>(() => {
      return realMoneyBalancePermissions.value[
        MGA_REAL_MONEY_BALANCE_PERMISSIONS.REPORT_PERMISSION_GET_MGA_PLAYER_REPORT_CSV
      ];
    });

    onMounted(() => {
      getRealMoneyBalancePlayerList();
    });

    onUnmounted(() => {
      setDefaultFilterFormData();
      setDefaultPaginationSettings();
      setDefaultSorting();
      setReportPeriod();
      setActiveFiltersCount(1);
      setFilterVisible(false);
    });

    return {
      realMoneyBalancePlayerTable,
      icons,
      realMoneyBalancePlayerList,
      isRealMoneyBalancePlayerListLoading,
      currentPage,
      itemsPerPage,
      PAGINATION_PROPERTIES,
      listItemsCount,
      isTwoFactorDialogVisible,
      realMoneyBalancePermissions,
      isTimestampAllowed,
      isReportDownloadAllowed,
      updateListSorting,
      updatePaginationLimit,
      updateCurrentPage,
      getRealMoneyBalancePlayerList,
      openTwoFactorDialog,
      getRealMoneyBalancePlayerListCsv,
      isFilterVisible,
      toggleFilterVisibility,
      activeFiltersCount,
      reportPeriod,
    };
  },
});
